const DISPLAY_TZ_KEY = 'ace-display-timezone';

export function getDisplayTimezone(): string {
  try {
    return window.localStorage.getItem(DISPLAY_TZ_KEY) || Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
}

export function setDisplayTimezone(tz: string): void {
  try {
    window.localStorage.setItem(DISPLAY_TZ_KEY, tz);
  } catch {
    // ignore
  }
}

/**
 * Parse a bare date+time string using an explicit source timezone.
 * e.g. parseInTimezone("2025-06-07", "20:00", "Europe/Amsterdam") → Date at that wall-clock moment in AMS.
 * Falls back to treating it as local time when the source timezone is missing.
 */
export function parseRoundDateTime(date: string, time: string, sourceTimezone?: string): Date | null {
  if (!date || !time) return null;
  const isoString = `${date}T${time}:00`;
  if (sourceTimezone) {
    try {
      // Use Intl to figure out the UTC offset for this wall-clock moment in the source tz.
      // We do a two-step: parse naively, then compute the offset and shift.
      const naive = new Date(isoString);
      if (Number.isNaN(naive.getTime())) return null;

      // Build a formatter that renders a UTC timestamp as if it were in sourceTimezone,
      // then find the offset by comparing.
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: sourceTimezone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      });

      // Binary search for the UTC time whose sourceTimezone representation equals isoString.
      // Simpler: format a candidate and compare; iterate at most twice to converge.
      let candidate = naive;
      for (let i = 0; i < 3; i++) {
        const parts = formatter.formatToParts(candidate);
        const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
        const rendered = `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
        const renderedDate = new Date(rendered);
        const drift = renderedDate.getTime() - naive.getTime();
        candidate = new Date(candidate.getTime() - drift);
      }
      return candidate;
    } catch {
      // Fall through to naive parse
    }
  }
  const naive = new Date(isoString);
  return Number.isNaN(naive.getTime()) ? null : naive;
}

/**
 * Parse a full ISO datetime string (with or without offset) and return a Date.
 */
export function parseIsoDateTime(value?: string): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a Date as a short date+time in the user's display timezone.
 * e.g. "Jun 7, 2025 · 20:00"
 */
export function formatDateTime(date: Date | null, displayTimezone: string): string {
  if (!date) return 'TBD';
  try {
    const datePart = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: displayTimezone });
    const timePart = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', timeZone: displayTimezone });
    return `${datePart} · ${timePart}`;
  } catch {
    return date.toLocaleString();
  }
}

/**
 * Format a Date as a short date (no time) in the user's display timezone.
 */
export function formatDate(date: Date | null, displayTimezone: string): string {
  if (!date) return 'TBD';
  try {
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: displayTimezone });
  } catch {
    return date.toLocaleDateString();
  }
}

/**
 * Format a countdown from now to the target date.
 */
export function formatCountdown(target: Date | null): string {
  if (!target) return 'No upcoming race';
  const diffMs = target.getTime() - Date.now();
  if (diffMs <= 0) return 'Now';
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/** Returns a sorted list of IANA timezone names for the timezone selector. */
export function getCommonTimezones(): string[] {
  return [
    'Pacific/Honolulu',
    'America/Anchorage',
    'America/Los_Angeles',
    'America/Denver',
    'America/Chicago',
    'America/New_York',
    'America/Sao_Paulo',
    'Atlantic/Azores',
    'Europe/London',
    'Europe/Amsterdam',
    'Europe/Berlin',
    'Europe/Paris',
    'Europe/Rome',
    'Europe/Helsinki',
    'Europe/Moscow',
    'Asia/Dubai',
    'Asia/Karachi',
    'Asia/Kolkata',
    'Asia/Bangkok',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
    'Pacific/Auckland',
  ];
}
